import { action, flow, makeObservable, observable } from 'mobx';
import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { BILL_TRANSACTION, CounterPartyExpensesTab } from '../types/types';
import { SpecialServicingBaseStore } from './specialServicingBaseStore';
import { downloadDocument } from '@roc/feature-utils';

export class ReceivablesBillsStore extends SpecialServicingBaseStore {
  detailsModalOpen: boolean;
  selectedRecord: any;
  totalReceivablesAmount: number
  receivableDetailsGridStore: GridStore;
  constructor(globalStore: GlobalStore) {
    super(globalStore);
    this.tab = CounterPartyExpensesTab.PENDING_RECEIVABLE_BILL;
    this.receivableDetailsGridStore = new GridStore(
      () => this.fetchReceivableDetails(),
      null,
      50
    );
    makeObservable(this, {
      fetchEntityNames: flow,
      performBillAction: flow,
      downloadBillDocuments: flow,
      exportBillInvoicesData: flow,
      updateComments: flow,
      downloadBillDocumentsBulk: flow,
      downloadInvoicesReport: flow,
      detailsModalOpen: observable,
      openModal: action,
      closeModal: action,
      selectedRecord: observable,
      totalReceivablesAmount: observable,
      removeReceivableFromBill: flow,
    });
  };

  private async fetchReceivableDetails() {
    try {
      const filters = {
        ...this.receivableDetailsGridStore.gridData.meta.filters
      };
      const response: ApiResponse = await this.service.getCounterPartyExpensesData(
        CounterPartyExpensesTab.RECEIVABLE_BILL_RECEIVABLES,
        this.selectedRecord?.recordId.toString(),
        this.receivableDetailsGridStore.gridData.meta.pageNumber,
        this.receivableDetailsGridStore.gridData.meta.pageSize,
        this.receivableDetailsGridStore.gridData.meta.sortDir,
        this.receivableDetailsGridStore.gridData.meta.sortBy,
        filters,
        this.receivableDetailsGridStore.gridData.meta.dropdownFilters,
      );
      this.totalReceivablesAmount = parseFloat(response.data.data.rows.reduce((total, payable) => total + payable.amount, 0).toFixed(2));
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching receivables bills detail.',
      });
    }
  };

  *fetchEntityNames() {
    try {
      const response: ApiResponse = yield this.service.getInvestorNames();
      this.entityList = response.data.data.map(value => ({
        label: value.name,
        value: value.companyId
      }));
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching investor names.',
      });
    }
  };


  reset() {
    this.tab = CounterPartyExpensesTab.BILL_RECEIVABLE;
    this.counterPartyExpenseGridStore.reset();
  };

  *downloadBillDocumentsBulk() {
    if (this.recordsIds && this.recordsIds.length > 0) {
      yield this.downloadBillDocuments(this.recordsIds);
    } else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Please select an item from the table.',
      });
    }
  };

  *downloadBillDocuments(servicingBillIds: number[]) {
    try {
      const response: ApiResponse = yield this.service.downloadBillDocuments(servicingBillIds);
      downloadDocument(response.data, response.headers, 'download', `${BILL_TRANSACTION}_DOCUMENT_BUNDLE.zip`);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while downloading servicing bill document bundle.',
      });
    }
  };

  *downloadInvoicesReport() {
    if (this.recordsIds && this.recordsIds.length > 0) {
      try {
        const response: ApiResponse = yield this.service.downloadInvoicesReport(this.recordsIds);
        downloadDocument(response.data, response.headers, 'download', `${BILL_TRANSACTION}_INVOICES_REPORT_BUNDLE.zip`);
      } catch (error) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Error while downloading servicing bill invoicers Report bundle.',
        });
      }
    } else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Please select an item from the table.',
      });
    }

  };



  *exportBillInvoicesData(servicingBillId: string) {
    try {
      const response: ApiResponse = yield this.service.exportServicingDashboardData(
        BILL_TRANSACTION,
        servicingBillId,
        null,
        null
      );
      downloadDocument(response.data, response.headers, 'download', `${BILL_TRANSACTION}_REPORT.xlsx`);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while exporting special servicing dashboard data.',
      });
    }
  };

  performActions(status: string) {
    throw Error('Not Implemented!');
  }

  performAction(id: number, status: string) {
    throw Error('Not Implemented!');
  }

  *performBillAction(id: number, status: string, date = null) {
    try {
      const response = yield this.service.performReceivableBillsAction(id, status, date);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Status updated successfully.'
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating status.',
      });
    }
  }

  *updateComments(id: number, comments: string, type: string) {
    try {
      const response = yield this.service.updateComments(id, comments, type);
      if (response.data.status === 'OK' || response?.data?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Comments updated successfully.'
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating comments.',
      });
    }
  }

  openModal(vendorPayment: any) {
    this.detailsModalOpen = true;
    this.selectedRecord = vendorPayment;
  }

  closeModal() {
    this.detailsModalOpen = false;
  }

  *removeReceivableFromBill(payableId: number, comment: string) {
    try {
      const response = yield this.service.removeReceivableFromBill(payableId, comment);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Receivable removed successfully.'
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while removing receivable.',
      });
    }
  }
}
